import React, {memo, useEffect, useState} from 'react';
import {DefStrNA} from "../../common/fusion-ui/config/FusionConfig";
import {SearchableSelect} from "../../common/fusion-ui";
import {printError, printLog} from "../../common/CommonUtils";
import {AdminReqUrls} from "../config/AdminConfig";
import {checkNullArr, checkNullStr, getArrIndexValue, getJsonValue, isBoolTrue} from "../../common/fusion-ui/utils/ReactUtils";
import {sendAdminRequest} from "../utils/AdminUtils";
import {useTheme} from "../../common/ProjectThemeProvider";

/**
 * Create country, state, city searchable-select component including their fetch-requests.
 * @example
 *
 * // Usage example:
 *  <CscSelect
 *           className={``}
 *           countryClassName={``}
 *           stateClassName={``}
 *           cityClassName={``}
 *           fetchDefData={fetchCscDataNow}
 *           defCountryId={getField(FormFields.countryName)}
 *           defStateId={getField(FormFields.stateName)}
 *           defCityId={getField(FormFields.cityName)}
 *           onCountryUpdate={(data, label, id) => {
 *               updateField(FormFields.countryName, id);
 *             // logTest('CscSelect.onCountryUpdate.data:', data, 'label:', label, 'id:', id);
 *           }}
 *           onStateUpdate={(data, label, id) => {
 *               updateField(FormFields.stateName, id);
 *             // logTest('CscSelect.onStateUpdate.data:', data, 'label:', label, 'id:', id);
 *           }}
 *           onCityUpdate={(data, label, id) => {
 *               updateField(FormFields.cityName, id);
 *             // logTest('CscSelect.onCityUpdate.data:', data, 'label:', label, 'id:', id);
 *           }}
 *  />
 *
 * @param {Object} props - The component's properties.
 * @param {string} props.className - Additional class for the component container.
 * @param {string} props.countryClassName - Additional class for the country dropdown.
 * @param {string} props.stateClassName - Additional class for the state dropdown.
 * @param {string} props.cityClassName - Additional class for the city dropdown.
 * @param {Object} props.style - Inline styles for the component container.
 * @param {boolean} props.showCountry - Specifies whether the country dropdown should be displayed.
 * @param {boolean} props.showState - Specifies whether the state dropdown should be displayed.
 * @param {boolean} props.showCity - Specifies whether the city dropdown should be displayed.
 * @param {boolean} props.fetchDefData - Specifies whether to fetch default data for the dropdowns.
 * @param {string | number} props.defCountryId - Default selected country ID/value for default-selection.
 * @param {string | number} props.defStateId - Default selected state ID/value for default-selection.
 * @param {string | number} props.defCityId - Default selected city ID/value for default-selection.
 * @param {function} props.onCountryUpdate({value: '', label: {}}, label, value) - Callback function triggered when the country selection changes.
 * @param {function} props.onStateUpdate({value: '', label: {}}, label, value) - Callback function triggered when the state selection changes.
 * @param {function} props.onCityUpdate({value: '', label: {}}, label, value) - Callback function triggered when the city selection changes.
 * @param {boolean} props.addParent - Specifies whether to include parent information in the dropdown options.
 * @param {boolean} props.hidden - Specifies whether the component should be hidden.
 * @param {boolean} props.searchableCountry - Specifies whether the country dropdown should be searchable or just a simple select-box.
 * @param {boolean} props.searchableState - Specifies whether the state dropdown should be searchable or just a simple select-box.
 * @param {boolean} props.searchableCity - Specifies whether the city dropdown should be searchable or just a simple select-box.
 */
/**
 Create a country, state, and city selection component that includes fetch requests for data.
 @component
 @param {Object} props - Component properties.
 @param {string} [props.className=""] - CSS classes for the component container.
 @param {string} [props.countryClassName=""] - CSS classes for the country dropdown.
 @param {string} [props.stateClassName=""] - CSS classes for the state dropdown.
 @param {string} [props.cityClassName=""] - CSS classes for the city dropdown.
 @param {Object} [props.style={}] - Inline styles for the component container.
 @param {boolean} [props.showCountry=true] - Display the country dropdown.
 @param {boolean} [props.showState=true] - Display the state dropdown.
 @param {boolean} [props.showCity=true] - Display the city dropdown.
 @param {boolean} [props.fetchDefData=false] - Fetch default data for the dropdowns.
 @param {string|number} [props.defCountryId] - Default selected country ID.
 @param {string|number} [props.defStateId] - Default selected state ID.
 @param {string|number} [props.defCityId] - Default selected city ID.
 @param {function} [props.onCountryUpdate] - Callback when the country selection changes.
 @param {function} [props.onStateUpdate] - Callback when the state selection changes.
 @param {function} [props.onCityUpdate] - Callback when the city selection changes.
 @param {boolean} [props.addParent=false] - Include parent information in dropdown options.
 @param {boolean} [props.hidden=false] - Hide the component.
 @param {boolean} [props.required] - Country, state, and city dropdowns required.
 @param {boolean} [props.requiredCountry] - Country dropdown required.
 @param {boolean} [props.requiredState] - State dropdown required.
 @param {boolean} [props.requiredCity] - City dropdown required.
 @param {Object} [props.countryWrapperStyle={}] - Inline styles for the country dropdown wrapper.
 @param {Object} [props.stateWrapperStyle={}] - Inline styles for the state dropdown wrapper.
 @param {Object} [props.cityWrapperStyle={}] - Inline styles for the city dropdown wrapper.
 @param {Object} [props.itemWrapperStyle={}] - Inline styles for the dropdown wrapper.
 */
const CscSelect = memo(({
                            className = "",
                            countryClassName = "",
                            itemClassName = "",
                            stateClassName = "",
                            cityClassName = "",

                            style = {},

                            showCountry = true,
                            showState = true,
                            showCity = true,

                            fetchDefData = false,

                            defCountryId,
                            defStateId,
                            defCityId,

                            onCountryUpdate,
                            onStateUpdate,
                            onCityUpdate,

                            addParent = false,
                            hidden = false,
                            required,
                            requiredCountry,
                            requiredState,
                            requiredCity,

                            countryWrapperStyle = {},
                            stateWrapperStyle = {},
                            cityWrapperStyle = {},
                            itemWrapperStyle = {},

                            // searchableCountry = true,
                            // searchableState = true,
                            // searchableCity = true,
                        }) => {
    const TAG = "CscSelect";

    const theme = useTheme();

    const NA_PLAIN = 'NA';
    const Fields = {
        countries: 'countries',
        states: 'states',
        cities: 'cities',
        country: 'country',
        state: 'state',
        city: 'city',

        cntrsFetched: 'cntrsFetched',
        statesFetched: 'statesFetched',
        citiesFetched: 'citiesFetched',
    }
    const [fieldsData, setFieldsData] = useState({
        [Fields.countries]: [],
        [Fields.states]: [],
        [Fields.cities]: [],
        [Fields.country]: {},
        [Fields.state]: {},
        [Fields.city]: {}
    });

    useEffect(() => {

    }, []);
    useEffect(() => {
        if (isBoolTrue(fetchDefData)) {
            // log('useEffect.fetchDefData.defCountryId:', defCountryId, 'defStateId:', defStateId, 'defCityId:', defCityId);
            Promise.all([
                (isBoolTrue(showCountry)) && fetchCountries(defCountryId),
                (isBoolTrue(showState) && checkNullStr(defCountryId)) && fetchStates(defCountryId, defStateId),
                (isBoolTrue(showCity) && checkNullStr(defStateId)) && fetchCities(defStateId, defCityId),
            ]);
        }
        // }, [fetchDefData, defCountryId, defStateId, defCityId]);
    }, [fetchDefData, showCountry, showState, showCity]);

    return (<>
        {(!isBoolTrue(hidden)) && (isBoolTrue(addParent) ? <div
            className={`p-0 m-0 row ${className}`}
            style={{...style}}>
            {createPanel()}
        </div> : createPanel())}
    </>)

    function createPanel() {
        return <>
            {isBoolTrue(showCountry) && <div style={{...itemWrapperStyle, ...countryWrapperStyle,}}>
                <SearchableSelect
                    label={'Country'}
                    labelAsPlaceholder={true}
                    wrapperClassName={`fd-column pt-1 pb-3 ${itemClassName} ${countryClassName}`}
                    className={`w-100 m-0 mt-1`}
                    style={{height: '24px !important'}}
                    labelColor={theme.text40}
                    multiple={false}
                    searchable={true}
                    required={isBoolTrue(required) || isBoolTrue(requiredCountry)}
                    justifyAt={'start'}
                    labelClassName={'fw-bold fs-sm text-start w-100'}
                    options={checkNullArr(getField(Fields.countries)) ? getField(Fields.countries) : []}
                    disabled={checkNullArr(getField(Fields.countries)) && !checkNullArr(getField(Fields.countries).filter(item => checkNullStr(getJsonValue(item, 'country')) && getJsonValue(item, 'country') != DefStrNA && getJsonValue(item, 'country') != NA_PLAIN))}
                    dropdownHeight={'100px'}
                    // defOption={getField(Fields.country)}
                    defOption={getFormattedCountry(getJsonValue(getField(Fields.country), 'value'))}
                    onChange={(data) => {
                        const newCountry = getFormattedCountry(data.value);
                        updateField(Fields.country, newCountry);
                        // log('country.Select.onChange.data:', data, 'newCountry:', newCountry);
                        if (onCountryUpdate) onCountryUpdate(data.value, data.label, data);
                        fetchStates(data.value, null, false);
                    }}
                />
            </div>}

            {/*State select*/}
            {isBoolTrue(showState) && <div style={{...itemWrapperStyle, ...stateWrapperStyle,}}>
                <SearchableSelect
                    label={'State'}
                    labelAsPlaceholder={true}
                    wrapperClassName={`fd-column pt-1 pb-3 ${itemClassName} ${stateClassName}`}
                    className={`w-100 m-0 mt-1`}
                    style={{height: '24px !important'}}
                    labelColor={theme.text40}
                    multiple={false}
                    searchable={true}
                    required={isBoolTrue(required) || isBoolTrue(requiredState)}
                    justifyAt={'start'}
                    labelClassName={'fw-bold fs-sm text-start w-100'}
                    options={checkNullArr(getField(Fields.states)) ? getField(Fields.states) : []}
                    disabled={checkNullArr(getField(Fields.states)) && !checkNullArr(getField(Fields.states).filter(item => checkNullStr(getJsonValue(item, 'state')) && getJsonValue(item, 'state') != DefStrNA && getJsonValue(item, 'state') != NA_PLAIN))}
                    dropdownHeight={'100px'}
                    defOption={getFormattedState(getJsonValue(getField(Fields.state), 'value'))}
                    onChange={(data) => {
                        const newState = getFormattedState(data.value);
                        updateField(Fields.state, newState);
                        // log('state.Select.onChange.data:', data, 'newState:', newState);
                        if (onStateUpdate) onStateUpdate(data.value, data.label, data);
                        fetchCities(data.value, null, false);
                    }}
                />
            </div>}

            {/*City select*/}
            {isBoolTrue(showCity) && <div style={{...itemWrapperStyle, ...cityWrapperStyle}}>
                <SearchableSelect
                    label={'City'}
                    labelAsPlaceholder={true}
                    wrapperClassName={`fd-column pt-1 pb-3 ${itemClassName} ${cityClassName}`}
                    className={`w-100 m-0 mt-1`}
                    style={{height: '24px !important'}}
                    labelColor={theme.text40}
                    multiple={false}
                    searchable={true}
                    required={isBoolTrue(required) || isBoolTrue(requiredCity)}
                    justifyAt={'start'}
                    labelClassName={'fw-bold fs-sm text-start w-100'}
                    options={checkNullArr(getField(Fields.cities)) ? getField(Fields.cities) : []}
                    disabled={checkNullArr(getField(Fields.cities)) && !checkNullArr(getField(Fields.cities).filter(item => checkNullStr(getJsonValue(item, 'city')) && getJsonValue(item, 'city') != DefStrNA && getJsonValue(item, 'city') != NA_PLAIN))}
                    dropdownHeight={'100px'}
                    defOption={getFormattedCity(getJsonValue(getField(Fields.city), 'value'))}
                    onChange={(data) => {
                        const newCity = getFormattedCity(data.value);
                        updateField(Fields.city, newCity);
                        // log('city.Select.onChange.data:', data, 'newCity:', newCity);
                        if (onCityUpdate) onCityUpdate(data.value, data.label, data);
                    }}
                />
            </div>}
        </>
    }

    async function fetchCountries(defCountryId, checkNullBefore = true) {
        const fun = 'fetchCountries:';
        try {
            if (!checkNullBefore || (checkNullBefore && !isFieldTrue(Fields.cntrsFetched))) {
                updateField(Fields.cntrsFetched, true);
                updateField(Fields.countries, []);
                updateField(Fields.states, []);
                updateField(Fields.cities, []);
                updateField(Fields.country, {});
                updateField(Fields.state, {});
                updateField(Fields.city, {});

                // log(fun, `Fetching countries...`);
                const REQUEST_OPTIONS = {};
                sendAdminRequest({
                    reqUrl: AdminReqUrls.fetchCountries,
                    reqOptions: REQUEST_OPTIONS,
                    onFetched: ((fetchedData) => {
                        // log(fun, 'fetchedData:', fetchedData);
                        let recordList = getJsonValue(fetchedData, 'data', []);
                        const success = checkNullArr(recordList);
                        if (success) {
                            recordList = recordList.map((item) => ({
                                value: item.id,
                                label: item.name,
                                id: item.id,
                                country: item.name
                            }));
                            updateField(Fields.countries, recordList);
                            if (checkNullStr(defCountryId)) updateField(Fields.country, getFormattedCountry(defCountryId, recordList));
                        }
                    }),
                    onError: ((error) => {
                        logErr(fun, error);
                    })
                });
            }
        } catch (e) {
            logErr(e);
        }
    }

    async function fetchStates(countryId, defStateId, checkNullBefore = true) {
        const fun = 'fetchStates:';
        try {
            if (!checkNullBefore || (checkNullBefore && !isFieldTrue(Fields.statesFetched))) {
                updateField(Fields.statesFetched, true);
                updateField(Fields.states, []);
                updateField(Fields.cities, []);
                updateField(Fields.state, {});
                updateField(Fields.city, {});

                // log(fun, `Fetching states...`);
                const REQUEST_OPTIONS = {country: countryId};
                sendAdminRequest({
                    reqUrl: AdminReqUrls.fetchStates,
                    reqOptions: REQUEST_OPTIONS,
                    onFetched: ((fetchedData) => {
                        // log(fun, 'fetchedData:', fetchedData);
                        let recordList = getJsonValue(fetchedData, 'data', []);
                        const success = checkNullArr(recordList);
                        if (success) {
                            recordList = recordList.map((item) => ({
                                value: item.id,
                                label: item.name,
                                id: item.id,
                                state: item.name
                            }));
                            updateField(Fields.states, recordList);
                            if (checkNullStr(defStateId)) updateField(Fields.state, getFormattedState(defStateId, recordList));
                        }
                    }),
                    onError: ((error) => {
                        logErr(fun, error);
                    })
                });
            }
        } catch (e) {
            logErr(e);
        }
    }

    async function fetchCities(stateId, defCityId, checkNullBefore = true) {
        const fun = 'fetchCities:';
        try {
            if (!checkNullBefore || (checkNullBefore && !isFieldTrue(Fields.citiesFetched))) {
                updateField(Fields.citiesFetched, true);
                updateField(Fields.cities, []);
                updateField(Fields.city, {});

                // log(fun, `Fetching cities...`);
                const REQUEST_OPTIONS = {state: stateId};
                sendAdminRequest({
                    reqUrl: AdminReqUrls.fetchCities,
                    reqOptions: REQUEST_OPTIONS,
                    onFetched: ((fetchedData) => {
                        // log(fun, 'fetchedData:', fetchedData);
                        let recordList = getJsonValue(fetchedData, 'data', []);
                        const success = checkNullArr(recordList);
                        if (success) {
                            recordList = recordList.map((item) => ({
                                value: item.id,
                                label: item.name,
                                id: item.id,
                                city: item.name
                            }));
                            updateField(Fields.cities, recordList);
                            if (checkNullStr(defCityId)) updateField(Fields.city, getFormattedCity(defCityId, recordList));
                        }
                    }),
                    onError: ((error) => {
                        logErr(fun, error);
                    })
                });
            }
        } catch (e) {
            logErr(e);
        }
    }


    function getFormattedCountry(countryId, countries = getField(Fields.countries)) {
        return getFormattedFieldValue(countryId, countries);
    }

    function getFormattedState(stateId, states = getField(Fields.states)) {
        return getFormattedFieldValue(stateId, states);
    }

    function getFormattedCity(cityId, cities = getField(Fields.cities)) {
        return getFormattedFieldValue(cityId, cities);
    }

    function getFormattedFieldValue(fieldId, allValues) {
        const fun = 'getFormattedFieldValue:';
        let value = {};
        try {
            // log(fun, 'field:', fieldCol, 'allValues:', allValues, 'fieldId:', fieldId);
            if (checkNullArr(allValues)) {
                let filteredItems = allValues.filter(item => (item.id == fieldId));
                value = getArrIndexValue(filteredItems, 0);//{id, value, label, country/state/city id}
                value = {value: getJsonValue(value, 'id'), label: getJsonValue(value, 'label')};
                // log(fun, 'field:', fieldCol, 'allValues:', allValues);
                // log(fun, 'field:', fieldCol, 'filteredItems:', filteredItems, ' value.label:', value.label, ' value.value:', value.value);
            }
        } catch (e) {
            logErr(fun, e)
        }
        return value;
    }

    function updateField(field, value) {
        setFieldsData(prevState => ({...prevState, [field]: value}));
    }

    function getField(field, defValue) {
        return getJsonValue(fieldsData, field, defValue)
    }

    function isFieldTrue(field, defValue) {
        return isBoolTrue(getField(field, defValue));
    }

    function log(...text) {
        printLog(TAG, ...text);
    }

    function logErr(...text) {
        printError(TAG, ...text);
    }
});

export default CscSelect;
